import { FC, useMemo, useState } from 'react';

import { Loader } from '@storybook';

import 'views/design-system/tokens.scss';
import './approval-channel.scss';

/**
 * The approval-link screen, shared by the two steps that send one:
 *
 *   MOBILE_CODE          — straight after the credentials step (the Choose
 *                          Method screen left the flow on 2026-09-15)
 *   BIOMETRIC_APPROVAL   — "Set up a passkey" on the passkey card; parked,
 *                          unreachable since Choose Method went
 *
 * It replaced a screen that told everyone "we have sent you a text message …
 * and an email" — a claim the frontend had no way to make (see
 * `_design/specs/approval-channel-chooser.md`), and which was false wherever
 * the SMS never went out. The person now CHOOSES the channel first, only that
 * channel is requested, and the waiting state names the one that was.
 *
 * Two phases, one card:
 *
 *   choose   — nothing sent yet. Two radio tiles (text / email, each with the
 *              identifier it would go to), a primary button that says what it
 *              will do, and Back.
 *   waiting  — one channel sent. A `sent` row naming it, the spinner, a switch
 *              to the other channel, Back, and the resend footer the old screen
 *              had (resend / countdown / support after the third resend).
 *
 * The step components keep every line of their own network and polling logic
 * and hand this screen callbacks; it owns only the choice and the copy.
 *
 * Always ADDITIVE against the old screen: title, spinner, Back, Resend, the
 * countdown and the Intercom fallback are all still here — only the copy and
 * the ordering of the send changed.
 *
 * Styling is `.sv2-scope` + tokens, the same family as `passkey-setup`. The
 * login shell does not claim dark mode, so in production this renders light;
 * the dark palette is still declared so it follows the shell if that changes.
 */

export type ApprovalChannelKind = 'phone' | 'email';
export type ApprovalPurpose = 'signin' | 'passkey';

export interface IResendState {
	/** the countdown has run out and the person may resend */
	allowed: boolean;
	/** seconds left on the countdown (0 when allowed) */
	seconds: number;
	/** the third resend has been used — offer support instead */
	exhausted: boolean;
}

interface IApprovalChannel {
	purpose: ApprovalPurpose;
	/** the email or phone the person typed at sign-in */
	identity: string;
	/** "+1 XXXXXX6179" — country code + masked phone, as the old screen showed it */
	phoneDisplay: string;
	/** masked email, or undefined when the frontend has never seen it */
	emailDisplay?: string;
	/** the channel that has been sent, or null while choosing */
	sent: ApprovalChannelKind | null;
	/**
	 * The link is spent — the poll answered status 'expired' (7-day TTL swept the
	 * record, or it had already been used). Shows the chooser again with an
	 * explanation instead of leaving the person on a card that says "sent".
	 */
	expired?: boolean;
	/** the channel currently being sent, or null */
	busy: ApprovalChannelKind | null;
	resend: IResendState;
	/** initial send, or a switch to the other channel */
	onSend: (channel: ApprovalChannelKind) => void;
	/** resend on the channel already sent */
	onResend: () => void;
	onBack: () => void;
	onSupport: () => void;
}

/** `austin.trombley@satschel.com` → `a***y@satschel.com` (never the full address on screen) */
export const maskEmail = (email: string): string => {
	const at = email.indexOf('@');
	if (at <= 0) return email;
	const local = email.slice(0, at);
	const domain = email.slice(at);
	if (local.length === 1) return `${local}***${domain}`;
	return `${local[0]}***${local[local.length - 1]}${domain}`;
};

const CHANNEL = {
	phone: {
		label: 'Text message',
		icon: 'ri-smartphone-line',
		sendLabel: 'Send by text message',
		switchLabel: 'Send it as a text instead',
		sentVerb: 'sent to',
		open: 'Tap the link in the text message',
		missing: "Didn't get the text message?",
	},
	email: {
		label: 'Email',
		icon: 'ri-mail-line',
		sendLabel: 'Send by email',
		switchLabel: 'Send it by email instead',
		sentVerb: 'sent to',
		open: 'Open the link in the email',
		missing: "Didn't get the email?",
	},
} as const;

const other = (c: ApprovalChannelKind): ApprovalChannelKind =>
	c === 'phone' ? 'email' : 'phone';

const pad = (n: number) => (n < 10 ? `0${n}` : `${n}`);

export const ApprovalChannel: FC<IApprovalChannel> = ({
	purpose,
	identity,
	phoneDisplay,
	emailDisplay,
	sent,
	expired = false,
	busy,
	resend,
	onSend,
	onResend,
	onBack,
	onSupport,
}) => {
	// When a link expires the person almost always wants the same channel again,
	// so it starts preselected — one click to resend rather than re-choosing.
	const [picked, setPicked] = useState<ApprovalChannelKind | null>(
		expired ? sent : null
	);

	const emailShown = emailDisplay ?? 'the address on your account';
	const target = (c: ApprovalChannelKind) =>
		c === 'phone' ? phoneDisplay : emailShown;

	const approves = useMemo(
		() =>
			purpose === 'passkey'
				? 'approves adding a passkey to this browser'
				: 'approves this sign-in',
		[purpose]
	);

	const subline = useMemo(() => {
		if (!identity) return '';
		return purpose === 'passkey'
			? `Adding a passkey for ${identity}`
			: `Signing in as ${identity}`;
	}, [identity, purpose]);

	const anyBusy = busy !== null;

	// ---------------------------------------------------------------- choose
	if (!sent || expired) {
		const tile = (c: ApprovalChannelKind) => {
			const checked = picked === c;
			return (
				<button
					type="button"
					role="radio"
					aria-checked={checked}
					className={`ac-tile${checked ? ' on' : ''}`}
					onClick={() => setPicked(c)}
					disabled={anyBusy}
					data-testid={`approval-channel-${c}`}
				>
					<span className="ac-tile-icon" aria-hidden="true">
						<i className={CHANNEL[c].icon} />
					</span>
					<span className="ac-tile-text">
						<span className="ac-tile-label">{CHANNEL[c].label}</span>
						<span className="ac-tile-to">{target(c)}</span>
					</span>
					<span className="ac-tile-check" aria-hidden="true">
						<i className="ri-check-line" />
					</span>
				</button>
			);
		};

		return (
			<div
				className="sv2-scope approval-channel"
				data-phase={expired ? 'expired' : 'choose'}
			>
				<div className="ac-card">
					<div className="ac-head">
						<div
							className={`ac-icon${expired ? ' warn' : ''}`}
							aria-hidden="true"
						>
							<i className={expired ? 'ri-time-line' : 'ri-send-plane-line'} />
						</div>
						<div className="ac-head-text">
							<div className="ac-title">
								{expired
									? 'That approval link has expired'
									: 'How should we send your approval link?'}
							</div>
							{subline && <div className="ac-sub">{subline}</div>}
						</div>
					</div>

					<div
						className="ac-tiles"
						role="radiogroup"
						aria-label="Send the approval link by"
					>
						{tile('phone')}
						{tile('email')}
					</div>

					<p className="ac-body">
						{expired
							? 'Links stop working after a while, or once they have been used. Send a fresh one and open that instead.'
							: `Either one ${approves}. You can switch to the other on the next screen.`}
					</p>

					<div className="ac-actions">
						<button
							type="button"
							className="ac-btn pri"
							onClick={() => picked && onSend(picked)}
							disabled={!picked || anyBusy}
							data-testid="approval-send"
						>
							<i
								className={
									busy ? 'ri-loader-4-line ac-spin' : 'ri-send-plane-line'
								}
								aria-hidden="true"
							/>
							{busy
								? 'Sending…'
								: picked
								? expired
									? `Send a new ${CHANNEL[picked].label.toLowerCase()}`
									: CHANNEL[picked].sendLabel
								: 'Send approval link'}
						</button>
						<button
							type="button"
							className="ac-btn"
							onClick={onBack}
							disabled={anyBusy}
							data-testid="approval-back"
						>
							Back
						</button>
					</div>
				</div>
			</div>
		);
	}

	// --------------------------------------------------------------- waiting
	const alt = other(sent);

	return (
		<div className="sv2-scope approval-channel" data-phase="waiting">
			<div className="ac-card" role="status" aria-live="polite">
				<div className="ac-head">
					<div className="ac-icon" aria-hidden="true">
						<i className="ri-time-line" />
					</div>
					<div className="ac-head-text">
						<div className="ac-title">Waiting for approval</div>
						{subline && <div className="ac-sub">{subline}</div>}
					</div>
				</div>

				<div className="ac-sent" data-testid="approval-sent">
					<span className="ac-sent-icon" aria-hidden="true">
						<i className={CHANNEL[sent].icon} />
					</span>
					<span className="ac-sent-text">
						<strong>{CHANNEL[sent].label}</strong> {CHANNEL[sent].sentVerb}{' '}
						<span className="ac-sent-to">{target(sent)}</span>
					</span>
					<span className="ac-chip">sent</span>
				</div>

				<p className="ac-body">
					{CHANNEL[sent].open} — it {approves}. This screen updates on its own
					once you have.
				</p>

				<div className="ac-wait" aria-hidden="true">
					<Loader className="loader-blue" dimension={60} />
				</div>

				<div className="ac-actions">
					<button
						type="button"
						className="ac-btn"
						onClick={() => onSend(alt)}
						disabled={anyBusy}
						data-testid={`approval-switch-${alt}`}
					>
						<i
							className={
								busy === alt ? 'ri-loader-4-line ac-spin' : CHANNEL[alt].icon
							}
							aria-hidden="true"
						/>
						{busy === alt ? 'Sending…' : CHANNEL[alt].switchLabel}
					</button>
					<button
						type="button"
						className="ac-btn"
						onClick={onBack}
						disabled={anyBusy}
						data-testid="approval-back"
					>
						Back
					</button>
				</div>

				{/* One recovery group: look in spam (email only), resend, or switch
				    channel (the button above). The spam line is subordinate —
				    a small secondary line, never a banner — and is not shown
				    for a text message, where it would make no sense. */}
				<div className="ac-recover">
					{sent === 'email' && (
						<div className="ac-hint" data-testid="approval-spam-hint">
							<i className="ri-spam-2-line" aria-hidden="true" />
							<span>Can&apos;t find it? Check your spam or junk folder.</span>
						</div>
					)}
					{resend.exhausted ? (
						<button
							type="button"
							className="ac-support"
							onClick={onSupport}
							data-testid="approval-support"
						>
							<i className="ri-user-voice-line" aria-hidden="true" />
							Facing trouble? Click here for assistance.
						</button>
					) : (
						<div className="ac-foot">
							<span className="ac-foot-text">{CHANNEL[sent].missing}</span>
							{resend.allowed ? (
								<button
									type="button"
									className="ac-link"
									onClick={onResend}
									disabled={anyBusy}
									data-testid="approval-resend"
								>
									{busy === sent
										? 'Sending…'
										: `Resend the ${CHANNEL[sent].label.toLowerCase()}`}
								</button>
							) : (
								<span className="ac-foot-text" data-testid="approval-countdown">
									You can resend in 00:{pad(resend.seconds)}
								</span>
							)}
						</div>
					)}
				</div>
			</div>
		</div>
	);
};
